import React from "react";
import { Link } from "react-router-dom";
import { KeyRound, Pencil, Trash2 } from "lucide-react";
import DataTable, { createActionsColumn } from "../data-table";
import UserAvatar from "../ui/UserAvatar";

const formatDate = (dateValue) => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
};

const getStatusClasses = (status) => {
  const normalized = String(status || "").toLowerCase();

  if (normalized === "active") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }

  if (normalized === "inactive") {
    return "border-red-200 bg-red-50 text-red-700";
  }

  return "border-amber-200 bg-amber-50 text-amber-700";
};

const normalizeStatus = (statusValue) => {
  const normalized = String(statusValue || "").trim().toLowerCase();
  return normalized === "active" ? "Active" : "Inactive";
};

const getUserName = (user) => user.Name || user.name || "-";

function UserTable({
  users,
  onEditUser,
  onDeleteUser,
  onResetPassword,
  loading = false,
  filterContent,
  activeFilterCount,
  toolbarRight,
}) {
  const columns = [
    {
      key: "Name",
      label: "Name",
      sortable: true,
      searchable: true,
      accessor: getUserName,
      render: (name, user) => (
        <Link to={`/profile/${user.id}`} className="flex items-center gap-2 hover:underline">
          <UserAvatar name={name} src={user.avatarThumbUrl} seed={user.id} size="sm" />
          <span className="font-semibold text-app-primary">{name}</span>
        </Link>
      ),
    },
    {
      key: "LinkedEmployee",
      label: "Linked Employee",
      sortable: true,
      accessor: (u) => u.LinkedEmployee?.fullName || "",
      render: (_, user) =>
        user.LinkedEmployee ? (
          <Link to={`/hr/employees/${user.LinkedEmployee.id}`} className="text-app-secondary hover:underline">
            {user.LinkedEmployee.fullName}
            {user.LinkedEmployee.employeeCode && <span className="ml-1 font-mono text-xs text-app-muted">{user.LinkedEmployee.employeeCode}</span>}
          </Link>
        ) : (
          <span className="text-app-muted">Not linked</span>
        ),
    },
    { key: "Email", label: "Email", sortable: true, searchable: true, accessor: (u) => u.Email || u.email || "-" },
    { key: "Role", label: "Role", sortable: true, accessor: (u) => u.Role || u.role || u.Designation || "-" },
    {
      key: "JoiningDate",
      label: "Joining Date",
      sortable: true,
      accessor: (u) => u.JoiningDate || u.joiningDate,
      render: formatDate,
    },
    {
      key: "Status",
      label: "Status",
      sortable: true,
      accessor: (u) => normalizeStatus(u.Status || u.status),
      render: (status) => (
        <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${getStatusClasses(status)}`}>
          {status}
        </span>
      ),
    },
    createActionsColumn(
      (user) => [
        { label: "Edit", icon: Pencil, onSelect: () => onEditUser(user) },
        { label: "Reset password", icon: KeyRound, onSelect: () => onResetPassword(user) },
        { label: "Delete", icon: Trash2, tone: "danger", onSelect: () => onDeleteUser(user) },
      ],
      { getRowLabel: getUserName },
    ),
  ];

  return (
    <DataTable
      ariaLabel="Users"
      columns={columns}
      data={users}
      loading={loading}
      loadingRowCount={8}
      searchable
      searchPlaceholder="Search by name or email"
      filterable={Boolean(filterContent)}
      filterContent={filterContent}
      activeFilterCount={activeFilterCount}
      toolbarRight={toolbarRight}
      pagination
      defaultPageSize={10}
      labels={{ empty: "No data found." }}
    />
  );
}

export default UserTable;
